import { useMemo } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CHANNEL_TYPE,
  useDiscordChannels,
  useDiscordRoles,
  type DiscordChannel,
  type DiscordRole,
} from '@/lib/queries';

const TEXT_CHANNEL_TYPES = new Set<number>([
  CHANNEL_TYPE.GuildText,
  CHANNEL_TYPE.GuildAnnouncement,
]);

function findChannel(channels: DiscordChannel[] | undefined, id: string): DiscordChannel | null {
  return channels?.find((c) => c.id === id) ?? null;
}

function findRole(roles: DiscordRole[] | undefined, id: string): DiscordRole | null {
  return roles?.find((r) => r.id === id) ?? null;
}

// ─────────────── Single channel picker ───────────────

interface ChannelPickerProps {
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  typeFilter?: 'text' | 'all';
}

export function DiscordChannelPicker({
  value,
  onValueChange,
  placeholder = 'Pick a channel',
  disabled,
  typeFilter = 'text',
}: ChannelPickerProps) {
  const { data: channels } = useDiscordChannels();
  const filtered = useMemo(
    () =>
      (channels ?? []).filter((c) =>
        typeFilter === 'text' ? TEXT_CHANNEL_TYPES.has(c.type) : true,
      ),
    [channels, typeFilter],
  );
  const current = findChannel(channels, value);
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder}>
          {value ? (current ? `#${current.name}` : `${value} (removed)`) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {filtered.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            #{c.name}
          </SelectItem>
        ))}
        {value && !current && <SelectItem value={value}>{value} (removed)</SelectItem>}
      </SelectContent>
    </Select>
  );
}

// ─────────────── Single category picker ───────────────

interface CategoryPickerProps {
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function DiscordCategoryPicker({
  value,
  onValueChange,
  placeholder = 'Pick a category',
  disabled,
}: CategoryPickerProps) {
  const { data: channels } = useDiscordChannels();
  const categories = useMemo(
    () => (channels ?? []).filter((c) => c.type === CHANNEL_TYPE.GuildCategory),
    [channels],
  );
  const current = findChannel(channels, value);
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder}>
          {value ? (current ? current.name : `${value} (removed)`) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {categories.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name}
          </SelectItem>
        ))}
        {value && !current && <SelectItem value={value}>{value} (removed)</SelectItem>}
      </SelectContent>
    </Select>
  );
}

// ─────────────── Single role picker ───────────────

interface RolePickerProps {
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function DiscordRolePicker({
  value,
  onValueChange,
  placeholder = 'Pick a role',
  disabled,
}: RolePickerProps) {
  const { data: roles } = useDiscordRoles();
  const current = findRole(roles, value);
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder}>
          {value ? (current ? `@${current.name}` : `${value} (removed)`) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {(roles ?? []).map((r) => (
          <SelectItem key={r.id} value={r.id}>
            @{r.name}
          </SelectItem>
        ))}
        {value && !current && <SelectItem value={value}>{value} (removed)</SelectItem>}
      </SelectContent>
    </Select>
  );
}

// ─────────────── Multi pickers (DropdownMenu w/ checkboxes) ───────────────

interface MultiPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

function multiTriggerLabel(
  selected: string[],
  itemsById: Map<string, string>,
  placeholder: string,
): string {
  if (selected.length === 0) return placeholder;
  if (selected.length <= 2) {
    return selected.map((id) => itemsById.get(id) ?? `${id} (removed)`).join(', ');
  }
  return `${selected.length} selected`;
}

export function DiscordChannelMultiPicker({
  value,
  onChange,
  placeholder = 'Pick channels',
  disabled,
}: MultiPickerProps) {
  const { data: channels } = useDiscordChannels();
  const filtered = useMemo(
    () => (channels ?? []).filter((c) => TEXT_CHANNEL_TYPES.has(c.type)),
    [channels],
  );
  const byId = useMemo(
    () => new Map(filtered.map((c) => [c.id, `#${c.name}`] as const)),
    [filtered],
  );
  function toggle(id: string) {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className="truncate">{multiTriggerLabel(value, byId, placeholder)}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
        {filtered.map((c) => (
          <DropdownMenuCheckboxItem
            key={c.id}
            checked={value.includes(c.id)}
            onCheckedChange={() => toggle(c.id)}
            onSelect={(e) => e.preventDefault()}
          >
            #{c.name}
          </DropdownMenuCheckboxItem>
        ))}
        {value
          .filter((id) => !byId.has(id))
          .map((id) => (
            <DropdownMenuCheckboxItem
              key={id}
              checked
              onCheckedChange={() => toggle(id)}
              onSelect={(e) => e.preventDefault()}
            >
              {id} (removed)
            </DropdownMenuCheckboxItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DiscordRoleMultiPicker({
  value,
  onChange,
  placeholder = 'Pick roles',
  disabled,
}: MultiPickerProps) {
  const { data: roles } = useDiscordRoles();
  const items = roles ?? [];
  const byId = useMemo(() => new Map(items.map((r) => [r.id, `@${r.name}`] as const)), [items]);
  function toggle(id: string) {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className="truncate">{multiTriggerLabel(value, byId, placeholder)}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
        {items.map((r) => (
          <DropdownMenuCheckboxItem
            key={r.id}
            checked={value.includes(r.id)}
            onCheckedChange={() => toggle(r.id)}
            onSelect={(e) => e.preventDefault()}
          >
            @{r.name}
          </DropdownMenuCheckboxItem>
        ))}
        {value
          .filter((id) => !byId.has(id))
          .map((id) => (
            <DropdownMenuCheckboxItem
              key={id}
              checked
              onCheckedChange={() => toggle(id)}
              onSelect={(e) => e.preventDefault()}
            >
              {id} (removed)
            </DropdownMenuCheckboxItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
